import { useEffect, useState } from 'react';
import { X, Loader2, ChevronRight, ChevronLeft } from 'lucide-react';
import { useTerritoryStore } from '../../../store/territoryStore';
import type { Territory, RealignmentPreview } from '../../../types';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  'DC',
];

// ===================== Step indicators =====================

const STEPS = [
  { label: 'Select Territories' },
  { label: 'Edit Boundaries' },
  { label: 'Preview' },
  { label: 'Confirm' },
];

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center mb-6">
      {STEPS.map((step, idx) => {
        const done = idx < current;
        const active = idx === current;
        return (
          <div key={idx} className="flex items-center">
            <div
              className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold border-2 ${
                done
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : active
                  ? 'border-blue-600 text-blue-600 bg-white dark:bg-gray-800'
                  : 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-800'
              }`}
            >
              {done ? '✓' : idx + 1}
            </div>
            <span
              className={`ml-1.5 text-xs font-medium ${
                active ? 'text-blue-600 dark:text-blue-400' : done ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              {step.label}
            </span>
            {idx < STEPS.length - 1 && (
              <div className="mx-3 flex-1 h-px bg-gray-200 dark:bg-gray-700 min-w-[24px]" />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ===================== Boundary edit state =====================

interface TerritoryBoundary {
  territoryId: string;
  territoryName: string;
  states: string[];
}

// ===================== Step 1: Select Territories =====================

function Step1SelectTerritories({
  territories,
  selectedIds,
  onToggle,
}: {
  territories: Territory[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
}) {
  const stateTerritories = territories.filter(
    (t) => t.isActive && (t.territoryType === 'STATE' || t.territoryType === 'HYBRID')
  );

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Select one or more territories to modify. Only state-boundary territories can be realigned.
      </p>
      {stateTerritories.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500 italic">No state-boundary territories found.</p>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm divide-y divide-gray-100 dark:divide-gray-700 max-h-[400px] overflow-y-auto">
          {stateTerritories.map((t) => (
            <label
              key={t.id}
              className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedIds.has(t.id)}
                onChange={() => onToggle(t.id)}
                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
              />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t.name}</span>
                {t.region && (
                  <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">{t.region.name}</span>
                )}
              </div>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {t.states?.length ?? 0} state{t.states?.length !== 1 ? 's' : ''}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ===================== Step 2: Edit Boundaries =====================

function BoundaryEditor({
  boundary,
  onChange,
}: {
  boundary: TerritoryBoundary;
  onChange: (states: string[]) => void;
}) {
  const toggle = (code: string) => {
    const next = boundary.states.includes(code)
      ? boundary.states.filter((s) => s !== code)
      : [...boundary.states, code].sort();
    onChange(next);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{boundary.territoryName}</h4>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          {boundary.states.length} state{boundary.states.length !== 1 ? 's' : ''} selected
        </span>
      </div>

      {boundary.states.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {boundary.states.map((code) => (
            <span
              key={code}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
            >
              {code}
              <button
                type="button"
                onClick={() => toggle(code)}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-200"
                aria-label={`Remove ${code}`}
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-1">
        {US_STATES.map((code) => (
          <button
            key={code}
            type="button"
            onClick={() => toggle(code)}
            className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${
              boundary.states.includes(code)
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400'
            }`}
          >
            {code}
          </button>
        ))}
      </div>
    </div>
  );
}

function Step2EditBoundaries({
  boundaries,
  onChangeBoundary,
}: {
  boundaries: TerritoryBoundary[];
  onChangeBoundary: (territoryId: string, states: string[]) => void;
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Edit the state assignments for each selected territory.
      </p>
      {boundaries.map((b) => (
        <BoundaryEditor
          key={b.territoryId}
          boundary={b}
          onChange={(states) => onChangeBoundary(b.territoryId, states)}
        />
      ))}
    </div>
  );
}

// ===================== Step 3: Preview =====================

function Step3Preview({ preview }: { preview: RealignmentPreview }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Review the proposed changes before committing. Token expires at{' '}
        <span className="font-medium">
          {new Date(preview.expiresAt).toLocaleTimeString()}
        </span>
        .
      </p>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Territory
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Before States
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                After States
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {preview.changes.map((change) => {
              const added = change.afterStates.filter((s) => !change.beforeStates.includes(s));
              const removed = change.beforeStates.filter((s) => !change.afterStates.includes(s));
              const hasChanges = added.length > 0 || removed.length > 0;
              return (
                <tr key={change.territoryId} className={hasChanges ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                    {change.territoryName}
                    {!hasChanges && (
                      <span className="ml-2 text-xs text-gray-400 dark:text-gray-500 font-normal">no change</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex flex-wrap gap-1">
                      {change.beforeStates.length === 0 ? (
                        <span className="text-gray-400 dark:text-gray-500 text-xs italic">none</span>
                      ) : (
                        change.beforeStates.map((s) => (
                          <span
                            key={s}
                            className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${
                              removed.includes(s)
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 line-through'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                            }`}
                          >
                            {s}
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex flex-wrap gap-1">
                      {change.afterStates.length === 0 ? (
                        <span className="text-gray-400 dark:text-gray-500 text-xs italic">none</span>
                      ) : (
                        change.afterStates.map((s) => (
                          <span
                            key={s}
                            className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${
                              added.includes(s)
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-semibold'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                            }`}
                          >
                            {s}
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===================== Step 4: Confirm =====================

function Step4Confirm({ onConfirm, onBack, isCommitting }: {
  onConfirm: () => void;
  onBack: () => void;
  isCommitting: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
        <p className="text-sm text-yellow-800 dark:text-yellow-300 font-medium">
          This action will update territory boundaries and reassign schools accordingly.
        </p>
        <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
          This cannot be undone — existing contacts and activities will remain with their assigned reps,
          but new assignments will follow the updated boundaries.
        </p>
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          disabled={isCommitting}
          className="flex items-center gap-1.5 border border-gray-300 dark:border-gray-600 px-4 py-2 rounded-md text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          <ChevronLeft size={14} /> Back to Preview
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={isCommitting}
          className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-60"
        >
          {isCommitting && <Loader2 size={14} className="animate-spin" />}
          {isCommitting ? 'Committing...' : 'Commit Realignment'}
        </button>
      </div>
    </div>
  );
}

// ===================== Main Wizard =====================

export default function RealignmentWizard() {
  const { territories, isLoadingTerritories, fetchTerritories, previewRealignment, commitRealignment, isPreviewLoading } =
    useTerritoryStore();

  const [step, setStep] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [boundaries, setBoundaries] = useState<TerritoryBoundary[]>([]);
  const [preview, setPreview] = useState<RealignmentPreview | null>(null);
  const [isCommitting, setIsCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [committed, setCommitted] = useState(false);

  useEffect(() => {
    fetchTerritories();
  }, [fetchTerritories]);

  const toggleTerritory = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleNextFromStep1 = () => {
    if (selectedIds.size === 0) {
      setError('Select at least one territory.');
      return;
    }
    setError(null);

    // Build boundary state from selected territories
    const newBoundaries: TerritoryBoundary[] = Array.from(selectedIds).map((id) => {
      const t = territories.find((x) => x.id === id)!;
      return {
        territoryId: id,
        territoryName: t.name,
        states: t.states?.map((s) => s.stateCode) ?? [],
      };
    });
    setBoundaries(newBoundaries);
    setStep(1);
  };

  const handleChangeBoundary = (territoryId: string, states: string[]) => {
    setBoundaries((prev) =>
      prev.map((b) => (b.territoryId === territoryId ? { ...b, states } : b))
    );
  };

  const handleNextFromStep2 = async () => {
    setError(null);
    try {
      const changes = boundaries.map((b) => ({
        territoryId: b.territoryId,
        states: b.states,
      }));
      const result = await previewRealignment(changes);
      setPreview(result);
      setStep(2);
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'Failed to generate preview.');
    }
  };

  const handleCommit = async () => {
    if (!preview) return;
    setIsCommitting(true);
    setError(null);
    try {
      await commitRealignment(preview.token);
      setCommitted(true);
      setStep(3);
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'Failed to commit realignment.');
    } finally {
      setIsCommitting(false);
    }
  };

  const handleReset = () => {
    setStep(0);
    setSelectedIds(new Set());
    setBoundaries([]);
    setPreview(null);
    setError(null);
    setCommitted(false);
  };

  if (committed) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center space-y-4">
        <p className="text-lg font-semibold text-green-700 dark:text-green-400">Realignment committed successfully.</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Territory boundaries have been updated. Map data has been refreshed.
        </p>
        <button
          type="button"
          onClick={handleReset}
          className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700"
        >
          Start New Realignment
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-4">
      <StepIndicator current={step} />

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2">
          {error}
        </p>
      )}

      {/* Step content */}
      {step === 0 && (
        <>
          {isLoadingTerritories ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading territories...
            </div>
          ) : (
            <Step1SelectTerritories
              territories={territories}
              selectedIds={selectedIds}
              onToggle={toggleTerritory}
            />
          )}
          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={handleNextFromStep1}
              disabled={selectedIds.size === 0}
              className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              Next: Edit Boundaries <ChevronRight size={14} />
            </button>
          </div>
        </>
      )}

      {step === 1 && (
        <>
          <Step2EditBoundaries
            boundaries={boundaries}
            onChangeBoundary={handleChangeBoundary}
          />
          <div className="flex justify-between mt-4">
            <button
              type="button"
              onClick={() => { setError(null); setStep(0); }}
              className="flex items-center gap-1.5 border border-gray-300 dark:border-gray-600 px-4 py-2 rounded-md text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ChevronLeft size={14} /> Back
            </button>
            <button
              type="button"
              onClick={handleNextFromStep2}
              disabled={isPreviewLoading}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-60"
            >
              {isPreviewLoading && <Loader2 size={14} className="animate-spin" />}
              {isPreviewLoading ? 'Generating Preview...' : <>Next: Preview <ChevronRight size={14} /></>}
            </button>
          </div>
        </>
      )}

      {step === 2 && preview && (
        <>
          <Step3Preview preview={preview} />
          <div className="flex justify-between mt-4">
            <button
              type="button"
              onClick={() => { setError(null); setStep(1); }}
              className="flex items-center gap-1.5 border border-gray-300 dark:border-gray-600 px-4 py-2 rounded-md text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ChevronLeft size={14} /> Back
            </button>
            <button
              type="button"
              onClick={() => setStep(3)}
              className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700"
            >
              Proceed to Confirm <ChevronRight size={14} />
            </button>
          </div>
        </>
      )}

      {step === 3 && (
        <Step4Confirm
          onConfirm={handleCommit}
          onBack={() => { setError(null); setStep(2); }}
          isCommitting={isCommitting}
        />
      )}
    </div>
  );
}
